import { createFileRoute, useNavigate } from "@tanstack/react-router"

import type { Attendance } from "@/features/records/api"
import { RecordsList, type EntityFilter } from "@/features/records/records-list"
import { RECORD_STATUSES } from "@/lib/constants"

/**
 * What the register is looking at, in the URL.
 *
 * Four filters live here rather than in component state, so an event screen, a
 * learner's history or the dashboard can link straight into a narrowed
 * register, and so the narrowed view can be shared, bookmarked and put in a
 * message. Page, sort and search stay in component state: they are how one
 * person reads a list, not what the list is.
 *
 * **No date range**, deliberately and not by omission. `GET /api/records` takes
 * `q`, `status`, `signed_off`, `event_id`, `employee_id`, `ordering` and the
 * paging pair, and nothing else; a date parameter in the URL that the request
 * could not carry would be a filter the screen appears to honour and does not,
 * which is the one failure a reader cannot detect. It arrives here the day the
 * endpoint grows one.
 *
 * Unrecognised values are dropped rather than passed on: a hand-edited URL
 * asking for an attendance word the server does not have would otherwise turn
 * a shared link into a 422.
 */
export interface RecordsSearch {
  event_id?: number
  employee_id?: number
  status?: Attendance
  signed_off?: boolean
}

function identifier(value: unknown) {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

/** Only the three attendance words the endpoint accepts. */
function attendance(value: unknown): Attendance | undefined {
  return typeof value === "string" &&
    (RECORD_STATUSES as readonly string[]).includes(value)
    ? (value as Attendance)
    : undefined
}

/**
 * A URL carries text, and the router hands back whatever was parsed from it, so
 * both forms are accepted and everything else is absent rather than false.
 */
function flag(value: unknown): boolean | undefined {
  if (value === true || value === "true") return true
  if (value === false || value === "false") return false
  return undefined
}

export const Route = createFileRoute("/records/")({
  validateSearch: (search: Record<string, unknown>): RecordsSearch => {
    const eventId = identifier(search.event_id)
    const employeeId = identifier(search.employee_id)
    const status = attendance(search.status)
    const signedOff = flag(search.signed_off)
    return {
      ...(eventId ? { event_id: eventId } : {}),
      ...(employeeId ? { employee_id: employeeId } : {}),
      ...(status ? { status } : {}),
      ...(signedOff === undefined ? {} : { signed_off: signedOff }),
    }
  },
  component: RecordsScreen,
})

function RecordsScreen() {
  const search = Route.useSearch()
  const navigate = useNavigate()

  function clear(filter: EntityFilter) {
    const next: RecordsSearch = { ...search }
    delete next[filter]
    void navigate({ to: "/records", search: next, replace: true })
  }

  /**
   * A filter set to nothing leaves the URL rather than sitting in it as an
   * empty value: `?status=` would travel to somebody else as a filter that
   * looks set and filters nothing.
   *
   * `replace`, because narrowing a list is a refinement of where you already
   * are. The three view pills are links instead, which push: a view is a
   * destination, and the back button should return from it.
   */
  function setFilters(next: {
    status?: Attendance | null
    signed_off?: boolean | null
  }) {
    const merged: RecordsSearch = { ...search }
    if ("status" in next) {
      if (next.status == null) delete merged.status
      else merged.status = next.status
    }
    if ("signed_off" in next) {
      if (next.signed_off == null) delete merged.signed_off
      else merged.signed_off = next.signed_off
    }
    void navigate({ to: "/records", search: merged, replace: true })
  }

  return (
    <RecordsList
      eventId={search.event_id ?? null}
      employeeId={search.employee_id ?? null}
      status={search.status ?? null}
      signedOff={search.signed_off ?? null}
      onFilterChange={setFilters}
      onClearEntityFilter={clear}
      onOpenRecord={(id) =>
        void navigate({ to: "/records/$recordId", params: { recordId: String(id) } })
      }
    />
  )
}
